import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Edit, Trash2, Download, Plus, X, Pill, Search, AlertTriangle, Users, CheckCircle, XCircle, PauseCircle, ChevronLeft, ChevronRight, Upload, FileText, CheckCheck, AlertCircle, Siren } from 'lucide-react';
import axios from 'axios';
import { NODE_API_URL } from '../config';

// API_URL is now managed in src/config.js
const API_URL = NODE_API_URL;

const Patients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1. INITIALIZE STATE
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('patients_search') || '');
  const [entriesPerPage, setEntriesPerPage] = useState(() => parseInt(sessionStorage.getItem('patients_entries')) || 10);
  const [currentPage, setCurrentPage] = useState(() => parseInt(sessionStorage.getItem('patients_page')) || 1);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null);

  // ✅ IMPORT STATE VARIABLES
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: Upload, 2: Preview, 3: Confirm
  const [importPreviewPage, setImportPreviewPage] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [importData, setImportData] = useState({ headers: [], preview: [], totalRecords: 0, csvFile: '' });
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importError, setImportError] = useState('');

  // ✅ CLEAN DB STATE
  const [showCleanModal, setShowCleanModal] = useState(false);
  const [cleanConfirmText, setCleanConfirmText] = useState('');
  const [cleanLoading, setCleanLoading] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '', phone_number: '', dob: '', address: '',
    insurance_provider: '', insurance_policy_no: '', status: 'Active',
    is_priority: false
  });



  useEffect(() => {
    sessionStorage.setItem('patients_search', searchTerm);
    sessionStorage.setItem('patients_entries', entriesPerPage);
    sessionStorage.setItem('patients_page', currentPage);
  }, [searchTerm, entriesPerPage, currentPage]);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/patients?_t=${Date.now()}`);
      if (Array.isArray(response.data)) {
        setPatients(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching patients:", error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // ✅ NEW: Status Badge Helper (Clean & Consistent)
  const getPatientStatusBadge = (status) => {
    const s = (status || 'Active').toLowerCase();

    if (s === 'active') {
      return {
        className: 'bg-green-100 text-green-700 border border-green-200',
        icon: <CheckCircle size={12} />
      };
    }
    if (s === 'inactive') {
      return {
        className: 'bg-gray-100 text-gray-600 border border-gray-200',
        icon: <XCircle size={12} />
      };
    }
    if (s === 'hold' || s === 'deceased') {
      return {
        className: 'bg-orange-100 text-orange-700 border border-orange-200',
        icon: <PauseCircle size={12} />
      };
    }
    if (s === 'f' || s === 'b' || s === 'u') {
      return {
        className: 'bg-teal-100 text-teal-800 border border-teal-200',
        icon: <Siren size={12} />
      };
    }
    // Default
    return {
      className: 'bg-gray-100 text-gray-600 border border-gray-200',
      icon: <XCircle size={12} />
    };
  };

  const formatID = (id) => String(id).padStart(4, '0');

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '';
    const cleanDate = dateString.split(' ')[0].split('T')[0];
    const [year, month, day] = cleanDate.split('-');
    return (year && month && day) ? `${month}/${day}/${year}` : dateString;
  };

  const filteredPatients = patients.filter(patient => {
    const search = searchTerm.toLowerCase();
    const cleanSearch = search.replace(/\D/g, '');

    const name = (patient.full_name || '').toLowerCase();
    const phone = (patient.phone_number || '').replace(/\D/g, '');
    const rxNumbers = (patient.rx_numbers || '').toLowerCase();
    const id = String(patient.id);

    // Smart Match: 
    // 1. Exact string match (Name, Notes, etc)
    // 2. Digit-only match (Phone, RX, ID)
    const nameMatch = (search.length >= 1 && search.length <= 2) ? name.startsWith(search) : name.includes(search);
    const phoneMatch = cleanSearch && phone.includes(cleanSearch);
    const rxMatch = (rxNumbers.includes(search) || (cleanSearch && rxNumbers.includes(cleanSearch)));
    const idMatch = cleanSearch && id.includes(cleanSearch);

    return nameMatch || phoneMatch || rxMatch || idMatch;
  });

  const totalEntries = filteredPatients.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [searchTerm, totalPages, currentPage]);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const handlePatientClick = (id) => {
    sessionStorage.setItem('lastViewedPatientId', id);
    navigate(`/patient/${id}`);
  };

  const openAddModal = () => {
    setIsEditing(false); setEditId(null);
    setFormData({
      full_name: '', phone_number: '', dob: '', address: '',
      insurance_provider: '', insurance_policy_no: '', status: 'Active',
      is_priority: false
    });
    setShowModal(true);
  };

  const handleEditClick = (e, patient) => {
    e.stopPropagation(); setIsEditing(true); setEditId(patient.id);
    let cleanDob = patient.dob ? patient.dob.split(' ')[0] : '';
    setFormData({
      full_name: patient.full_name, phone_number: patient.phone_number, dob: cleanDob,
      address: patient.address || '', insurance_provider: patient.insurance_provider,
      insurance_policy_no: patient.insurance_policy_no, status: patient.status || 'Active',
      is_priority: patient.is_priority === 1
    });
    setShowModal(true);
  };

  const confirmDeleteClick = (e, patient) => { e.stopPropagation(); setPatientToDelete(patient); setShowDeleteModal(true); };

  const executeDelete = async () => {
    if (!patientToDelete) return;
    try {
      await axios.post(`${API_URL}/patients/delete`, { id: patientToDelete.id });
      setShowDeleteModal(false); setPatientToDelete(null); fetchPatients();
    } catch (error) { console.error(error); alert("Error deleting patient"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = isEditing ? `${API_URL}/patients/update` : `${API_URL}/patients/add`;
    const payload = isEditing ? { ...formData, id: editId } : formData;
    try {
      const res = await axios.post(endpoint, payload);
      if (res.data.success) {
        alert(isEditing ? "Patient Updated!" : "Patient Registered!");
        setShowModal(false); fetchPatients();
      } else { alert("Error saving patient"); }
    } catch (error) { console.error(error); alert("System Error"); }
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(patients);
    XLSX.utils.book_append_sheet(wb, ws, "Patients");
    XLSX.writeFile(wb, "AG_Pharmacy_Patients.xlsx");
  };

  // ✅ IMPORT HANDLERS
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setImportLoading(true);
    setImportError('');
    setImportMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      let endpoint = '';
      if (file.type === 'application/pdf') {
        endpoint = `${NODE_API_URL}/import/pdf`;
      } else if (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        endpoint = `${NODE_API_URL}/import/csv`;
      } else {
        setImportError('Invalid file type. Please upload PDF or CSV files.');
        setImportLoading(false);
        return;
      }

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setImportData(response.data);
        setImportMessage(`✅ File processed! Found ${response.data.totalRecords} records.`);
        setImportStep(2);
      } else {
        setImportError(response.data.error || 'Failed to process file');
      }
    } catch (error) {
      setImportError(error.response?.data?.error || error.message || 'Error uploading file');
      console.error('Upload error:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    setImportLoading(true);
    setImportError('');

    try {
      // ✅ Send csvFile reference so the backend reads the saved CSV
      const response = await axios.post(`${NODE_API_URL}/import/to-database`, {
        csvFile: importData.csvFile
      });

      if (response.data.success) {
        const { imported, updated, skipped } = response.data.result;
        setImportMessage(`✅ ${imported} new imported, ${updated} updated, and ${skipped} unchanged.`);
        setImportStep(3); // Move to completion step

        // Refresh patient list
        setTimeout(() => {
          fetchPatients();
          setShowImportModal(false);
          setImportStep(1);
        }, 2000);
      } else {
        setImportError(response.data.error || 'Import failed');
      }
    } catch (error) {
      setImportError(error.response?.data?.error || error.message || 'Error importing data');
      console.error('Import error:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportStep(1);
    setImportError('');
    setImportMessage('');
    setUploadedFile(null);
    setImportData({ headers: [], preview: [], totalRecords: 0, csvFile: '' });
    setImportPreviewPage(1);
  };

  // ✅ CLEAN DATABASE HANDLER
  const handleCleanDatabase = async () => {
    if (cleanConfirmText !== 'CLEAN') return;

    setCleanLoading(true);
    try {
      const response = await axios.post(`${API_URL}/import/clean-database`);
      if (response.data.success) {
        alert("🧹 Database cleaned successfully!");
        setShowCleanModal(false);
        setCleanConfirmText('');
        fetchPatients();
      } else {
        alert("Error cleaning database: " + response.data.error);
      }
    } catch (error) {
      console.error("Clean error:", error);
      alert("System Error during cleaning");
    } finally {
      setCleanLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-3">
          <Users className="text-teal-600" size={30} />
          Patients List
        </h2>
        <div className="flex flex-col sm:flex-row gap-4 items-center w-full md:w-auto">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <input type="text" placeholder="Search Name or ID..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64" value={searchTerm} onChange={handleSearchChange} />
          </div>
          <button onClick={openAddModal} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center gap-2 transition shadow-md"><Plus size={16} /> Register</button>
          <button onClick={() => { setShowImportModal(true); setImportStep(1); setImportError(''); setImportMessage(''); }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2 transition shadow-md"><Upload size={16} /> Import</button>
          <button onClick={() => { setShowCleanModal(true); setCleanConfirmText(''); }} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center gap-2 transition shadow-md"><Trash2 size={16} /> Clean</button>
          <button onClick={handleDownloadExcel} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2 transition shadow-md"><Download size={16} /> Excel</button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select value={entriesPerPage} onChange={(e) => { setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option><option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        <div className="text-sm text-gray-600">Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries</div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">ID</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Patient Name</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Address</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Phone Number</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">DOB</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Insurance</th>
              <th className="px-6 py-3 text-center font-bold text-gray-500 uppercase">RX Count</th>
              <th className="px-6 py-3 text-center font-bold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (<tr><td colSpan="9" className="text-center py-4">Loading...</td></tr>) : paginatedPatients.length === 0 ? (<tr><td colSpan="9" className="text-center py-10 text-gray-400 italic">No patients found.</td></tr>) : (
              paginatedPatients.map((patient) => {
                // Get Badge Styles
                const badge = getPatientStatusBadge(patient.status);

                return (
                  <tr key={patient.id} id={`patient-row-${patient.id}`} className="hover:bg-teal-50 transition duration-150 cursor-pointer" onClick={() => handlePatientClick(patient.id)}>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono">{formatID(patient.id)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-teal-600 font-bold hover:underline">{patient.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700 max-w-xs truncate" title={patient.address}>{patient.address || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{patient.phone_number || <span className="text-gray-300 italic">-</span>}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{formatDate(patient.dob) || <span className="text-gray-300 italic">-</span>}</td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className="block text-gray-900 font-medium">{patient.insurance_provider}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-center"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${patient.rx_count > 0 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800'}`}><Pill size={12} className="mr-1" /> {patient.rx_count}</span></td>

                    {/* ✅ FIXED STATUS BADGE */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
                        {badge.icon} {patient.status || 'Active'}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap flex gap-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => handleEditClick(e, patient)} className="text-yellow-500 hover:text-yellow-600 bg-yellow-50 p-2 rounded"><Edit size={18} /></button>
                      <button onClick={(e) => confirmDeleteClick(e, patient)} className="text-red-500 hover:text-red-600 bg-red-50 p-2 rounded"><Trash2 size={18} /></button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination (Hidden for brevity, same as before) */}
      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"><ChevronLeft size={16} /> Previous</button>
          <div className="flex gap-1">{[...Array(totalPages)].map((_, idx) => { const pageNum = idx + 1; if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) { return (<button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`px-3 py-1.5 border rounded ${currentPage === pageNum ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 hover:bg-gray-50'}`}>{pageNum}</button>); } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) { return <span key={pageNum} className="px-2">...</span>; } return null; })}</div>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1">Next <ChevronRight size={16} /></button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-1/3 p-6 relative">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">{isEditing ? <Edit className="text-yellow-500" /> : <Plus className="text-teal-600" />} {isEditing ? 'Edit Patient Details' : 'New Patient Registration'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Full Name</label><input type="text" name="full_name" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.full_name} onChange={handleInputChange} /></div>

                {/* ✅ UPDATED STATUS DROPDOWN */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select name="status" className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-white" value={formData.status} onChange={handleInputChange}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Hold">Hold</option>
                    <option value="Deceased">Deceased</option>
                    <option value="F">Follow Up (F)</option>
                    <option value="B">Billing Issue (B)</option>
                    <option value="U">Urgent (U)</option>
                  </select>
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700">Home Address</label><input type="text" name="address" placeholder="Street, City, Zip" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.address} onChange={handleInputChange} /></div>
              <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700">Phone Number</label><input type="text" name="phone_number" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.phone_number} onChange={handleInputChange} /></div><div><label className="block text-sm font-medium text-gray-700">Date of Birth</label><input type="date" name="dob" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.dob} onChange={handleInputChange} /></div></div>
              <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                <input
                  type="checkbox"
                  id="is_priority"
                  name="is_priority"
                  className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  checked={formData.is_priority}
                  onChange={(e) => setFormData({ ...formData, is_priority: e.target.checked })}
                />
                <label htmlFor="is_priority" className="text-sm font-bold text-red-700 cursor-pointer">
                  Mark as High Priority
                </label>
              </div>

              <div><label className="block text-sm font-medium text-gray-700">Insurance Provider</label><input type="text" name="insurance_provider" placeholder="e.g. BlueCross" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.insurance_provider} onChange={handleInputChange} /></div>

              <button type="submit" className={`w-full text-white font-bold py-2 rounded transition mt-4 ${isEditing ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-teal-600 hover:bg-teal-700'}`}>{isEditing ? 'Update Patient' : 'Register Patient'}</button>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && patientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
            <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mt-2">Are you sure you want to delete <strong>{patientToDelete.full_name}</strong>?</p>
            <div className="mt-6 flex justify-center gap-3"><button onClick={() => setShowDeleteModal(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">Cancel</button><button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700">Delete</button></div>
          </div>
        </div>
      )}

      {/* ✅ IMPORT MODAL */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-6 relative">
            <button onClick={closeImportModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>

            {/* STEP INDICATOR */}
            <div className="flex justify-center gap-8 mb-6">
              <div className={`flex flex-col items-center ${importStep >= 1 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${importStep >= 1 ? 'bg-blue-600' : 'bg-gray-300'}`}>1</div>
                <span className="text-xs mt-2 font-medium">Upload</span>
              </div>
              <div className={`flex flex-col items-center ${importStep >= 2 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${importStep >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}>2</div>
                <span className="text-xs mt-2 font-medium">Preview</span>
              </div>
              <div className={`flex flex-col items-center ${importStep >= 3 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${importStep >= 3 ? 'bg-green-600' : 'bg-gray-300'}`}>✓</div>
                <span className="text-xs mt-2 font-medium">Complete</span>
              </div>
            </div>

            {/* ALERTS */}
            {importError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 items-start">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-sm text-red-700">{importError}</span>
              </div>
            )}
            {importMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex gap-2 items-start">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-sm text-green-700">{importMessage}</span>
              </div>
            )}

            {/* STEP 1: UPLOAD */}
            {importStep === 1 && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2"><Upload className="text-blue-600" /> Upload PDF or CSV</h3>
                <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-500 transition cursor-pointer relative">
                  <input
                    type="file"
                    accept=".pdf,.csv,.xls,.xlsx"
                    onChange={handleFileUpload}
                    disabled={importLoading}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  />
                  <FileText className="mx-auto text-blue-400 mb-3" size={48} />
                  <p className="text-sm text-gray-600">
                    {uploadedFile ? (
                      <>
                        <strong>{uploadedFile.name}</strong> selected
                      </>
                    ) : (
                      <>
                        Drag and drop or click to select<br />
                        <span className="text-xs text-gray-500">Supports PDF, CSV, Excel (max 50MB)</span>
                      </>
                    )}
                  </p>
                </div>
                {importLoading && <p className="text-center text-blue-600 mt-4 flex items-center justify-center gap-2">⏳ Processing file...</p>}
              </div>
            )}

            {/* STEP 2: PREVIEW */}
            {importStep === 2 && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2"><FileText className="text-blue-600" /> Preview Data</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Total Records:</strong> {importData.totalRecords}
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Columns:</strong> {importData.headers.join(', ')}
                  </p>
                </div>
                <div className="border rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        {importData.headers.map((header, idx) => (
                          <th key={idx} className="px-4 py-2 text-left text-gray-700 font-medium whitespace-nowrap">{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importData.preview.slice((importPreviewPage - 1) * 10, importPreviewPage * 10).map((row, idx) => (
                        <tr key={idx} className="border-t hover:bg-gray-50">
                          {importData.headers.map((header, hidx) => (
                            <td key={hidx} className="px-4 py-2 text-gray-700 whitespace-nowrap text-xs">{row[header] || '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-gray-500">
                    Showing {(importPreviewPage - 1) * 10 + 1} to {Math.min(importPreviewPage * 10, importData.totalRecords)} of {importData.totalRecords} records
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setImportPreviewPage(Math.max(1, importPreviewPage - 1))}
                      disabled={importPreviewPage === 1}
                      className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-xs font-medium content-center px-1 border rounded bg-gray-50 w-8 text-center">{importPreviewPage}</span>
                    <button
                      onClick={() => setImportPreviewPage(Math.min(Math.ceil(importData.totalRecords / 10), importPreviewPage + 1))}
                      disabled={importPreviewPage >= Math.ceil(importData.totalRecords / 10)}
                      className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: COMPLETE */}
            {importStep === 3 && (
              <div className="text-center">
                <CheckCheck className="mx-auto text-green-600 mb-4" size={64} />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Import Successful!</h3>
                <p className="text-gray-600">Your patients have been imported successfully.</p>
              </div>
            )}

            {/* BUTTONS */}
            <div className="flex gap-3 mt-6 justify-end">
              {importStep < 3 && (
                <button
                  onClick={closeImportModal}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
              )}
              {importStep === 2 && (
                <button
                  onClick={handleConfirmImport}
                  disabled={importLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {importLoading ? '⏳ Importing...' : <><CheckCircle size={16} /> Confirm Import</>}
                </button>
              )}
              {importStep === 3 && (
                <button
                  onClick={closeImportModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ CLEAN DATABASE MODAL (SAFETY FIRST) */}
      {showCleanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8 relative border-t-8 border-red-600 animate-in fade-in zoom-in duration-200">
            <button onClick={() => setShowCleanModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"><X size={24} /></button>

            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">CRITICAL ACTION</h3>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                This will <strong className="text-red-600">permanently delete all Patients, Prescriptions, and Prescribers</strong> from the database.
                Manual entries, PDF imports, and Directory records will be wiped. This action cannot be undone.
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 text-center">Type <span className="text-red-600">CLEAN</span> to confirm</label>
              <input
                type="text"
                className={`w-full text-center font-bold text-xl py-3 border-2 rounded-lg focus:outline-none transition-all ${cleanConfirmText === 'CLEAN' ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-200 text-red-600 focus:border-red-400'}`}
                placeholder="---"
                value={cleanConfirmText}
                onChange={(e) => setCleanConfirmText(e.target.value.toUpperCase())}
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleCleanDatabase}
                disabled={cleanConfirmText !== 'CLEAN' || cleanLoading}
                className={`w-full py-3 rounded-lg font-black text-white shadow-lg transition-all transform active:scale-95 ${cleanConfirmText === 'CLEAN' && !cleanLoading ? 'bg-red-600 hover:bg-red-700 cursor-pointer' : 'bg-gray-300 cursor-not-allowed'}`}
              >
                {cleanLoading ? '⌛ WIPING DATABASE...' : 'WIPE EVERYTHING'}
              </button>
              <button
                onClick={() => setShowCleanModal(false)}
                className="w-full py-2 text-gray-500 hover:text-gray-700 font-medium transition-colors"
              >
                Cancel, I'm not sure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;